import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, PlugZap, Trash2, ExternalLink, Info } from "lucide-react";
import { isPaidProvider, api, errMsg, fmtDate } from "@/lib/api";
import { CategoryBadge, PageHeader, StatusPill, credState } from "@/components/Common";

const ProviderForm = ({ p, cred, onChange }) => {
  const [f, setF] = useState({ api_key: "", account_id: cred?.extra?.account_id || "", base_url: cred?.extra?.base_url || "", default_model: cred?.default_model || "" });
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy("save");
    try {
      const body = { default_model: f.default_model };
      if (f.api_key) body.api_key = f.api_key;
      if (p.extra_fields.includes("account_id")) body.account_id = f.account_id;
      if (p.extra_fields.includes("base_url")) body.base_url = f.base_url;
      await api.put(`/credentials/${p.id}`, body);
      setF({ ...f, api_key: "" });
      toast.success(`${p.name} gespeichert`);
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/credentials/${p.id}/test`);
      data.ok ? toast.success(data.message) : toast.error(data.message);
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`Zugangsdaten für ${p.name} wirklich löschen?`)) return;
    await api.delete(`/credentials/${p.id}`);
    toast.success("Zugangsdaten gelöscht");
    setF({ api_key: "", account_id: "", base_url: "", default_model: "" });
    onChange();
  };

  return (
    <article id={p.id} className="glass scroll-mt-24 p-6" data-testid={`provider-card-${p.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><h3 className="text-lg font-semibold text-white">{p.name}</h3><CategoryBadge category={p.cost_status} /></div>
        <StatusPill state={credState(cred)} testid={`provider-status-${p.id}`} />
      </div>
      {isPaidProvider(p) && <p className="mt-2 text-xs text-red-300">Kostenpflichtig: wird nie automatisch genutzt, nur nach ausdrücklicher Bestätigung im Playground/API.</p>}
      {p.local && <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-400"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />Private/Loopback-Adressen (z. B. localhost) müssen zuerst vom Admin unter „Admin → Lokale Endpunkte“ freigegeben werden (SSRF-Schutz).</p>}
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {p.extra_fields.includes("base_url") && (
          <div><label className="label">Basis-URL</label><input data-testid={`provider-baseurl-${p.id}`} className="field mt-1.5" placeholder={p.id === "ollama" ? "http://ollama:11434" : "http://vllm:8000"} value={f.base_url} onChange={set("base_url")} /></div>
        )}
        {p.extra_fields.includes("account_id") && (
          <div><label className="label">Account-ID</label><input data-testid={`provider-account-${p.id}`} className="field mt-1.5" value={f.account_id} onChange={set("account_id")} /></div>
        )}
        <div>
          <label className="label">API-Key {p.key_required ? "" : "(optional)"}</label>
          <input data-testid={`provider-key-${p.id}`} type="password" autoComplete="off" className="field mt-1.5" placeholder={cred?.key_masked ? `Gespeichert: ${cred.key_masked}` : "Key einfügen"} value={f.api_key} onChange={set("api_key")} />
        </div>
        <div><label className="label">Standardmodell (optional)</label><input data-testid={`provider-model-${p.id}`} className="field mt-1.5" placeholder={p.default_model || "automatisch (erstes kostenloses)"} value={f.default_model} onChange={set("default_model")} /></div>
      </div>
      {cred?.last_test && (
        <div data-testid={`provider-test-result-${p.id}`} className={`mt-4 rounded-lg border px-3 py-2 text-xs ${cred.last_test.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200" : "border-red-500/30 bg-red-500/5 text-red-200"}`}>
          {cred.last_test.message} · {cred.last_test.duration_ms} ms · {fmtDate(cred.last_test.at)}
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        <button disabled={!!busy} onClick={save} data-testid={`provider-save-${p.id}`} className="btn-grad inline-flex items-center gap-1.5 px-4 py-2 text-sm"><Save className="h-4 w-4" />{busy === "save" ? "Speichere…" : "Speichern"}</button>
        <button disabled={!cred || !!busy} onClick={test} data-testid={`provider-test-${p.id}`} className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white disabled:opacity-40"><PlugZap className="h-4 w-4" />{busy === "test" ? "Teste…" : "Verbindung testen"}</button>
        {cred && <button onClick={remove} data-testid={`provider-delete-${p.id}`} className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-sm text-red-300"><Trash2 className="h-4 w-4" />Löschen</button>}
        {p.key_url && <a href={p.key_url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-sm text-blue-400">Key erzeugen <ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
    </article>
  );
};

export default function ProviderKeys() {
  const [providers, setProviders] = useState([]);
  const [creds, setCreds] = useState({});
  const load = async () => {
    const [c, k] = await Promise.all([api.get("/catalog"), api.get("/credentials")]);
    setProviders(c.data.providers);
    setCreds(Object.fromEntries(k.data.map((x) => [x.provider_id, x])));
  };
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    if (providers.length && window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  }, [providers]);

  return (
    <div data-testid="provider-keys-page">
      <PageHeader title="Provider & Keys" subtitle="Hinterlege deine eigenen Zugangsdaten. Keys werden verschlüsselt auf dem Server gespeichert und nur maskiert angezeigt. „Verbindung testen“ ruft den Provider wirklich auf (Modellliste, keine Tokens)." />
      <div className="grid gap-5 xl:grid-cols-2">
        {providers.map((p) => <ProviderForm key={p.id + (creds[p.id]?.updated_at || "")} p={p} cred={creds[p.id]} onChange={load} />)}
      </div>
    </div>
  );
}
